import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from './useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from './use-toast';
import { formatDateForDB } from '@/lib/dateUtils';
import type { TaskListWithStats } from '@/hooks/useTaskLists';

// Use exact same types as useTasks.tsx to maintain compatibility
export interface FamilyTask {
  id: string;
  family_id: string;
  title: string;
  description?: string;
  created_by: string;
  assigned_to: string;
  assigned_to_list?: string[];
  status: 'pending' | 'completed';
  completed_at?: string;
  completed_by?: string;
  created_at: string;
  updated_at: string;
  due_date?: string;
  list_id?: string;
  event_id?: string;
}

const CACHE_TIME = 10 * 60 * 1000; // 10 minutes
const STALE_TIME = 60 * 1000; // 1 minute for faster updates

export function useOptimizedTasks(familyId?: string) {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Query for tasks with optimized caching
  const tasksQuery = useQuery({
    queryKey: ['tasks', familyId],
    queryFn: async (): Promise<FamilyTask[]> => {
      if (!familyId) return [];

      const { data, error } = await supabase
        .from('family_tasks')
        .select('*')
        .eq('family_id', familyId)
        .order('display_order', { ascending: true })
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Process tasks to create virtual "General" list for orphaned shared tasks
      const processedTasks = await processTasksWithGeneralList(data || [], familyId);
      return processedTasks;
    },
    enabled: !!familyId,
    staleTime: 30 * 1000, // Keep data fresh for 30 seconds
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes during navigation
    refetchOnWindowFocus: true, // Refetch when window gains focus
  });

  // Helper function to process tasks - simplified since General list is handled in useTaskLists
  const processTasksWithGeneralList = async (tasks: FamilyTask[], familyId: string): Promise<FamilyTask[]> => {
    // Just return tasks as-is since orphaned task handling is now done in useTaskLists
    // This prevents duplication between the two hooks
    return tasks;
  };

  const createTaskMutation = useMutation({
    mutationFn: async ({
      title,
      description,
      assignedToList,
      dueDate,
      listId,
      eventId
    }: {
      title: string;
      description: string;
      assignedToList: string[];
      dueDate?: Date;
      listId?: string;
      eventId?: string;
    }) => {
      if (!familyId || !user) throw new Error('Missing familyId or user');

      // Get current user profile
      const { data: profile } = await supabase
        .from('profiles')
        .select('id')
        .eq('user_id', user.id)
        .single();

      if (!profile) throw new Error('Profile not found');

      // Parse assignment values (support both raw UUIDs and prefixed values like "profile:uuid" / "person:uuid")
      const parsed = (assignedToList || [])
        .filter(Boolean)
        .map((v) => (v.includes(':') ? { type: v.split(':')[0], id: v.split(':')[1] } : { type: 'profile', id: v }));
      const profileIds = parsed.filter(p => p.type === 'profile').map(p => p.id);
      const otherIds = parsed.filter(p => p.type !== 'profile').map(p => p.id);

      const primaryAssignee = profileIds[0] || profile.id;

      const { data, error } = await supabase
        .from('family_tasks')
        .insert([
          {
            family_id: familyId,
            title,
            description: description || null,
            created_by: profile.id,
            assigned_to: primaryAssignee,
            assigned_to_list: (() => { const all = Array.from(new Set([...profileIds, ...otherIds])); return all.length > 0 ? all : null; })(),
            due_date: dueDate ? formatDateForDB(dueDate) : null,
            list_id: listId || null,
            event_id: eventId || null,
          }
        ])
        .select('*')
        .single();

      if (error) throw error;
      return data;
    },
    onMutate: async (variables) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['tasks', familyId] });
      
      // Snapshot previous values
      const previousTasks = queryClient.getQueryData<FamilyTask[]>(['tasks', familyId]);
      const previousTaskLists = queryClient.getQueryData<TaskListWithStats[]>(['task-lists-with-stats', familyId]);
      
      // Optimistically update cache with new task
      if (previousTasks && user) {
        const optimisticTask: FamilyTask = {
          id: `temp-${Date.now()}`,
          family_id: familyId!,
          title: variables.title,
          description: variables.description || undefined,
          created_by: 'temp',
          assigned_to: 'temp',
          assigned_to_list: variables.assignedToList,
          status: 'pending',
          due_date: variables.dueDate ? formatDateForDB(variables.dueDate) : undefined,
          list_id: variables.listId,
          event_id: variables.eventId,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };
        
        queryClient.setQueryData<FamilyTask[]>(['tasks', familyId], [optimisticTask, ...previousTasks]);
        
        // Also optimistically update task lists with stats for instant UI feedback on /tasks
        if (variables.listId && previousTaskLists) {
          const optimisticPreview = {
            id: optimisticTask.id,
            title: optimisticTask.title,
            due_date: optimisticTask.due_date || null,
            assigned_profile_ids: variables.assignedToList || [],
          } as any;
          const updatedLists = previousTaskLists.map((list) => {
            if (list.id !== variables.listId) return list;
            const newPreviews = [optimisticPreview, ...(list.task_previews || [])].slice(0, 3);
            return {
              ...list,
              pending_count: (list.pending_count || 0) + 1,
              task_previews: newPreviews,
            };
          });
          queryClient.setQueryData<TaskListWithStats[]>(['task-lists-with-stats', familyId], updatedLists);
        }
      }
      
      return { previousTasks, previousTaskLists } as const;
    },
    onError: (err, variables, context) => {
      // Roll back on error
      if (context?.previousTasks) {
        queryClient.setQueryData(['tasks', familyId], context.previousTasks);
      }
      
      toast({
        title: "Erreur",
        description: "Impossible d'ajouter la tâche",
        variant: "destructive",
      });
    },
    onSuccess: (newTask) => {
      // Invalidate and refetch to get server state
      queryClient.invalidateQueries({ queryKey: ['tasks', familyId] });
      queryClient.invalidateQueries({ queryKey: ['task-lists', familyId] });
      queryClient.refetchQueries({ queryKey: ['tasks', familyId] });
      
      toast({
        title: "Tâche ajoutée",
        description: "La tâche a été ajoutée à votre to-do list",
      });
    },
  });

  // Mutation to complete task
  const completeTaskMutation = useMutation({
    mutationFn: async (taskId: string) => {
      if (!user) throw new Error('User not found');

      const { data: profile } = await supabase
        .from('profiles')
        .select('id')
        .eq('user_id', user.id)
        .single();

      if (!profile) throw new Error('Profile not found');

      const { data, error } = await supabase
        .from('family_tasks')
        .update({
          status: 'completed',
          completed_at: new Date().toISOString(),
          completed_by: profile.id
        })
        .eq('id', taskId)
        .select('*')
        .single();

      if (error) throw error;
      return data;
    },
    onMutate: async (taskId) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['tasks', familyId] });
      
      // Snapshot previous values
      const previousTasks = queryClient.getQueryData<FamilyTask[]>(['tasks', familyId]);
      const previousTaskLists = queryClient.getQueryData<TaskListWithStats[]>(['task-lists-with-stats', familyId]);
      
      // Optimistically update the task status
      if (previousTasks) {
        const updatedTasks = previousTasks.map(task => 
          task.id === taskId 
            ? { ...task, status: 'completed' as const, completed_at: new Date().toISOString() }
            : task
        );
        queryClient.setQueryData<FamilyTask[]>(['tasks', familyId], updatedTasks);

        // Also update task lists with stats for instant UI feedback
        if (previousTaskLists) {
          const taskBeingCompleted = previousTasks.find(t => t.id === taskId);
          if (taskBeingCompleted) {
            const updatedLists = previousTaskLists.map((list) => {
              if (list.id !== taskBeingCompleted.list_id) return list;
              return {
                ...list,
                pending_count: Math.max((list.pending_count || 1) - 1, 0),
                completed_count: (list.completed_count || 0) + 1,
                task_previews: (list.task_previews || []).filter(p => p.id !== taskId),
              };
            });
            queryClient.setQueryData<TaskListWithStats[]>(['task-lists-with-stats', familyId], updatedLists);
          }
        }
      }
      
      return { previousTasks, previousTaskLists } as const;
    },
    onError: (err, taskId, context) => {
      // Roll back on error
      if (context?.previousTasks) {
        queryClient.setQueryData(['tasks', familyId], context.previousTasks);
      }
      if (context?.previousTaskLists) {
        queryClient.setQueryData(['task-lists-with-stats', familyId], context.previousTaskLists);
      }
      
      toast({
        title: "Erreur",
        description: "Impossible de marquer la tâche comme terminée",
        variant: "destructive",
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks', familyId] });
      queryClient.invalidateQueries({ queryKey: ['task-lists-with-stats', familyId] });
      toast({
        title: "Tâche terminée",
        description: "La tâche a été marquée comme terminée",
      });
    },
  });

  // Mutation to update task
  const updateTaskMutation = useMutation({
    mutationFn: async ({ taskId, title, description, assignedToList, dueDate }: {
      taskId: string; title: string; description: string; assignedToList: string[]; dueDate?: Date;
    }) => {
      // Parse assignment values
      const parsed = (assignedToList || [])
        .filter(Boolean)
        .map((v) => (v.includes(':') ? { type: v.split(':')[0], id: v.split(':')[1] } : { type: 'profile', id: v }));
      const profileIds = parsed.filter(p => p.type === 'profile').map(p => p.id);
      const otherIds = parsed.filter(p => p.type !== 'profile').map(p => p.id);
      
      // Use first available ID as primary assignee (profile preferred, then person)
      const primaryAssignee = profileIds[0] || otherIds[0] || null;
      
      const { data, error } = await supabase
        .from('family_tasks')
        .update({
          title,
          description: description || null,
          assigned_to: primaryAssignee,
          assigned_to_list: (() => { const all = Array.from(new Set([...profileIds, ...otherIds])); return all.length > 0 ? all : null; })(),
          due_date: dueDate ? formatDateForDB(dueDate) : null,
        })
        .eq('id', taskId)
        .select('*')
        .single();

      if (error) throw error;
      return data;
    },
    onMutate: async (variables) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['tasks', familyId] });
      
      // Snapshot previous values
      const previousTasks = queryClient.getQueryData<FamilyTask[]>(['tasks', familyId]);
      const previousTaskLists = queryClient.getQueryData<TaskListWithStats[]>(['task-lists-with-stats', familyId]);
      
      // Optimistically update the task
      if (previousTasks) {
        const updatedTasks = previousTasks.map(task => 
          task.id === variables.taskId 
            ? { 
                ...task, 
                title: variables.title,
                description: variables.description || undefined,
                assigned_to_list: variables.assignedToList,
                due_date: variables.dueDate ? formatDateForDB(variables.dueDate) : undefined,
                updated_at: new Date().toISOString()
              }
            : task
        );
        queryClient.setQueryData<FamilyTask[]>(['tasks', familyId], updatedTasks);

        // Also update task lists with stats for instant UI feedback
        if (previousTaskLists) {
          const taskBeingUpdated = previousTasks.find(t => t.id === variables.taskId);
          if (taskBeingUpdated) {
            const optimisticPreview = {
              id: variables.taskId,
              title: variables.title,
              due_date: variables.dueDate ? formatDateForDB(variables.dueDate) : null,
              assigned_profile_ids: variables.assignedToList || [],
            } as any;

            const updatedLists = previousTaskLists.map((list) => {
              if (list.id !== taskBeingUpdated.list_id) return list;
              const newPreviews = (list.task_previews || []).map(p => 
                p.id === variables.taskId ? optimisticPreview : p
              );
              return {
                ...list,
                task_previews: newPreviews,
              };
            });
            queryClient.setQueryData<TaskListWithStats[]>(['task-lists-with-stats', familyId], updatedLists);
          }
        }
      }
      
      return { previousTasks, previousTaskLists } as const;
    },
    onError: (err, variables, context) => {
      // Roll back on error
      if (context?.previousTasks) {
        queryClient.setQueryData(['tasks', familyId], context.previousTasks);
      }
      if (context?.previousTaskLists) {
        queryClient.setQueryData(['task-lists-with-stats', familyId], context.previousTaskLists);
      }
      
      toast({
        title: "Erreur",
        description: "Impossible de modifier la tâche",
        variant: "destructive",
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks', familyId] });
      queryClient.invalidateQueries({ queryKey: ['task-lists-with-stats', familyId] });
      toast({ title: "Tâche modifiée" });
    },
  });

  // Mutation to delete task
  const deleteTaskMutation = useMutation({
    mutationFn: async (taskId: string) => {
      const { error } = await supabase.from('family_tasks').delete().eq('id', taskId);
      if (error) throw error;
    },
    onMutate: async (taskId) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['tasks', familyId] });
      
      // Snapshot previous values
      const previousTasks = queryClient.getQueryData<FamilyTask[]>(['tasks', familyId]);
      const previousTaskLists = queryClient.getQueryData<TaskListWithStats[]>(['task-lists-with-stats', familyId]);
      
      // Optimistically remove the task
      if (previousTasks) {
        const filteredTasks = previousTasks.filter(task => task.id !== taskId);
        queryClient.setQueryData<FamilyTask[]>(['tasks', familyId], filteredTasks);

        // Also update task lists with stats for instant UI feedback
        if (previousTaskLists) {
          const taskBeingDeleted = previousTasks.find(t => t.id === taskId);
          if (taskBeingDeleted) {
            const updatedLists = previousTaskLists.map((list) => {
              if (list.id !== taskBeingDeleted.list_id) return list;
              const isPending = taskBeingDeleted.status === 'pending';
              return {
                ...list,
                pending_count: isPending ? Math.max((list.pending_count || 1) - 1, 0) : list.pending_count,
                completed_count: !isPending ? Math.max((list.completed_count || 1) - 1, 0) : list.completed_count,
                task_previews: (list.task_previews || []).filter(p => p.id !== taskId),
              };
            });
            queryClient.setQueryData<TaskListWithStats[]>(['task-lists-with-stats', familyId], updatedLists);
          }
        }
      }
      
      return { previousTasks, previousTaskLists } as const;
    },
    onError: (err, taskId, context) => {
      // Roll back on error
      if (context?.previousTasks) {
        queryClient.setQueryData(['tasks', familyId], context.previousTasks);
      }
      if (context?.previousTaskLists) {
        queryClient.setQueryData(['task-lists-with-stats', familyId], context.previousTaskLists);
      }
      
      toast({
        title: "Erreur",
        description: "Impossible de supprimer la tâche",
        variant: "destructive",
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks', familyId] });
      queryClient.invalidateQueries({ queryKey: ['task-lists-with-stats', familyId] });
      toast({ title: "Tâche supprimée" });
    },
  });

  // Mutation to update tasks order
  const updateTasksOrderMutation = useMutation({
    mutationFn: async (orderedTaskIds: string[]) => {
      const updatePromises = orderedTaskIds.map((taskId, index) =>
        supabase
          .from('family_tasks')
          .update({ display_order: index })
          .eq('id', taskId)
      );

      const results = await Promise.all(updatePromises);
      const errors = results.filter(result => result.error);
      
      if (errors.length > 0) {
        throw new Error('Failed to update some task orders');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks', familyId] });
      queryClient.invalidateQueries({ queryKey: ['task-lists-with-stats', familyId] });
    },
    onError: () => {
      toast({
        title: "Erreur",
        description: "Impossible de réorganiser les tâches",
        variant: "destructive",
      });
    },
  });

  // Mutation to update task list
  const updateTaskListMutation = useMutation({
    mutationFn: async ({ taskId, listId }: { taskId: string; listId: string }) => {
      const { data, error } = await supabase
        .from('family_tasks')
        .update({ list_id: listId })
        .eq('id', taskId)
        .select('*')
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks', familyId] });
      queryClient.invalidateQueries({ queryKey: ['task-lists-with-stats', familyId] });
      toast({
        title: "Tâche déplacée",
        description: "La tâche a été déplacée vers une autre liste",
      });
    },
  });

  return {
    tasks: tasksQuery.data || [],
    loading: tasksQuery.isLoading,
    createTask: (title: string, description: string, assignedToList: string[], dueDate?: Date, listId?: string, eventId?: string) =>
      createTaskMutation.mutateAsync({ title, description, assignedToList, dueDate, listId, eventId }),
    completeTask: completeTaskMutation.mutateAsync,
    updateTask: (taskId: string, title: string, description: string, assignedToList: string[], dueDate?: Date) =>
      updateTaskMutation.mutateAsync({ taskId, title, description, assignedToList, dueDate }),
    deleteTask: deleteTaskMutation.mutate,
    updateTasksOrder: (orderedTaskIds: string[]) =>
      updateTasksOrderMutation.mutate(orderedTaskIds),
    updateTaskList: (taskId: string, listId: string) =>
      updateTaskListMutation.mutate({ taskId, listId }),
    refreshTasks: () => tasksQuery.refetch(),
  };
}